import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [demoSession, setDemoSession] = useState(null);
  const [showDemoFeedback, setShowDemoFeedback] = useState(false);

  const fetchUser = async () => {
    try {
      setAuthError(null);
      const response = await fetch('/.auth/me');
      if (response.ok) {
        const authData = await response.json();
        // Static Web Apps returns user info in clientPrincipal
        const userInfo = authData.clientPrincipal;
        if (userInfo) {
          const newUser = {
            id: userInfo.userId,
            email: userInfo.userDetails,
            name: userInfo.userClaims?.find(claim => claim.typ === 'name')?.val || userInfo.userDetails,
            provider: userInfo.identityProvider,
            roles: userInfo.userRoles
          };

          // Set flag ONLY if user state was null (transitioning from logged out to logged in)
          const wasLoggedOut = !user;
          if (wasLoggedOut) {
            console.log('[AUTH] User was logged out, setting just_logged_in flag');
            sessionStorage.setItem('just_logged_in', 'true');
          } else {
            console.log('[AUTH] User already logged in, NOT setting flag');
          }

          // CRITICAL: Sync OAuth data to backend BEFORE setting user state
          // This ensures the user exists in backend before dependent contexts
          // (like AdminContext) make API calls that require the user to exist
          const apiUrl = process.env.REACT_APP_API_URL || '';  // Empty string uses SWA proxy

          // Check for invite token from sessionStorage
          const inviteToken = sessionStorage.getItem('invite_token');

          try {
            // Build URL with optional invite_token query parameter
            let syncUrl = `${apiUrl}/api/profile/sync-oauth`;
            if (inviteToken) {
              syncUrl += `?invite_token=${encodeURIComponent(inviteToken)}`;
            }

            const syncResponse = await fetch(syncUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(authData)
            });
            if (!syncResponse.ok) {
              throw new Error(`Sync failed with status ${syncResponse.status}`);
            }

            // Store is_new_org flag from OAuth sync response for welcome overlay
            const syncData = await syncResponse.json();
            if (syncData.is_new_org) {
              sessionStorage.setItem('is_new_org', 'true');
            }

            // Track invite signup completion if token was used
            if (inviteToken && wasLoggedOut && window.gtag) {
              window.gtag('event', 'invite_signup_completed', {
                invite_token: inviteToken
              });
              // Clear token after successful signup
              sessionStorage.removeItem('invite_token');
            }
          } catch (error) {
            console.error('OAuth sync failed:', error);
            setAuthError('Login failed. Please try again.');
            setUser(null);
            return;
          }

          // NOW set user state - backend user exists, safe for dependent contexts
          setUser(newUser);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (provider = 'aad') => {
    // Redirect to OAuth provider
    window.location.href = `/.auth/login/${provider}`;
  };

  const logout = async () => {
    // Check if in demo mode
    if (demoSession) {
      logoutDemo();
      return;
    }

    // OAuth logout
    // Call backend to close session
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout API error:', e);
    }
    // Redirect to logout endpoint (with redirect back to app root)
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
  };

  const loginDemo = (demoData, prospectEmail) => {
    // demoData is the response from /api/demo/login (already called by DemoEmailModal)
    const session = {
      isDemoMode: true,
      prospectEmail: prospectEmail,
      expiresAt: new Date(demoData.expires_at).toISOString(),
      sessionId: demoData.token,
      user: demoData.user,
      organization: demoData.organization
    };

    sessionStorage.setItem('demoToken', demoData.token);
    sessionStorage.setItem('demoSession', JSON.stringify(session));

    setDemoSession(session);
    setUser(demoData.user);
    setOrganization(demoData.organization);
  };

  const logoutDemo = () => {
    sessionStorage.removeItem('demoToken');
    sessionStorage.removeItem('demoSession');
    setDemoSession(null);
    setUser(null);
    setOrganization(null);
    setShowDemoFeedback(false);
    // Redirect to login
    window.location.href = '/';
  };

  const requestDemoLogout = () => {
    // Show feedback modal instead of logging out directly
    setShowDemoFeedback(true);
  };

  const closeDemoFeedback = () => {
    setShowDemoFeedback(false);
  };

  useEffect(() => {
    // Check for existing demo session first
    const storedDemo = sessionStorage.getItem('demoSession');
    if (storedDemo) {
      try {
        const session = JSON.parse(storedDemo);
        if (new Date(session.expiresAt) > new Date()) {
          // Valid demo session
          setDemoSession(session);
          setUser(session.user);
          setOrganization(session.organization);
          setLoading(false);
          return;
        } else {
          // Expired demo session - clean up
          sessionStorage.removeItem('demoToken');
          sessionStorage.removeItem('demoSession');
        }
      } catch (e) {
        console.error('Failed to restore demo session:', e);
        sessionStorage.removeItem('demoToken');
        sessionStorage.removeItem('demoSession');
      }
    }

    // No valid demo session, check OAuth
    fetchUser();
  }, []);

  const clearAuthError = () => setAuthError(null);

  const value = {
    user,
    organization,
    loading,
    authError,
    clearAuthError,
    login,
    logout,
    loginDemo,
    logoutDemo,
    requestDemoLogout,
    demoSession,
    isDemoMode: !!demoSession,
    isAuthenticated: !!user,
    showDemoFeedback,
    closeDemoFeedback
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};